// Admin: list of all merchants with KYB status.

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Filter as FilterIcon, Loader2, MapPin, Store, Mail, Phone, ArrowRight, Download } from 'lucide-react';
import { toast } from 'sonner';
import { PortalShell } from '../../components/shared/PortalShell';
import { Card, EmptyState, StatusPill } from '../../components/shared/primitives';
import { MapView } from '../../components/shared/MapView';
import { Button } from '../../components/stripe-ui/Button';
import { ADMIN_NAV } from '../_navs';
import { admin, certificates, type AdminMerchantRow, type CertLang } from '../../lib/api';

type KybStatus = 'kyb_review' | 'approved' | 'rejected' | 'suspended';
type KybTone = 'success' | 'danger' | 'outline';

// Allowed next-steps per current status (mirrors the backend KYB_EDGES).
const KYB_ACTIONS: Record<string, Array<{ to: KybStatus; label: string; tone: KybTone }>> = {
  applied:    [{ to: 'approved', label: 'Approve', tone: 'success' }, { to: 'kyb_review', label: 'Review', tone: 'outline' }, { to: 'rejected', label: 'Reject', tone: 'danger' }],
  kyb_review: [{ to: 'approved', label: 'Approve', tone: 'success' }, { to: 'rejected', label: 'Reject', tone: 'danger' }],
  approved:   [{ to: 'suspended', label: 'Suspend', tone: 'danger' }],
  suspended:  [{ to: 'approved', label: 'Reinstate', tone: 'success' }],
  rejected:   [{ to: 'kyb_review', label: 'Re-open', tone: 'outline' }],
};

const FILTERS = [
  { value: 'all',         label: 'All' },
  { value: 'applied',     label: 'Applied' },
  { value: 'kyb_review',  label: 'KYB review' },
  { value: 'approved',    label: 'Approved' },
  { value: 'suspended',   label: 'Suspended' },
  { value: 'rejected',    label: 'Rejected' },
];

export function AdminMerchantsPage() {
  const q = useQuery({ queryKey: ['admin', 'merchants'], queryFn: () => admin.merchants() });
  const [filter, setFilter] = useState<string>('all');

  const rows = useMemo(() => {
    const list = q.data ?? [];
    if (filter === 'all') return list;
    return list.filter((m) => m.status === filter);
  }, [q.data, filter]);

  const mapMarkers = useMemo(() =>
    rows.filter((m) => m.latitude && m.longitude).map((m) => ({
      lat: Number(m.latitude), lng: Number(m.longitude),
      label: `${m.business_name} · ${m.city?.name ?? ''} · ${m.status}`,
      color: m.status === 'approved' ? '#1858bf' : (m.status === 'suspended' || m.status === 'rejected') ? '#dc2626' : '#d97706',
    })), [rows]);

  return (
    <PortalShell nav={ADMIN_NAV} activeId="merchants" title="Merchants" subtitle="Approve KYB applications. Suspend bad actors.">
      <div className="space-y-4">
        <Card padding="p-3" className="flex flex-wrap items-center gap-2">
          <FilterIcon className="w-4 h-4 text-slate-400" />
          {FILTERS.map((f) => (
            <button
              key={f.value}
              onClick={() => setFilter(f.value)}
              className={`px-3 py-1 rounded-full text-[12px] font-semibold transition-colors ${
                filter === f.value ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              {f.label}
            </button>
          ))}
        </Card>

        {mapMarkers.length > 0 && (
          <Card padding="p-0" className="overflow-hidden">
            <div className="px-4 py-2.5 border-b border-slate-100 flex items-center justify-between gap-2 flex-wrap">
              <p className="text-[12px] font-semibold text-slate-700 inline-flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-slate-400" /> Merchant coverage · {mapMarkers.length} located</p>
              <span className="text-[10.5px] text-slate-400">🔵 approved · 🟠 in review · 🔴 suspended/rejected</span>
            </div>
            <MapView height="340px" markers={mapMarkers} />
          </Card>
        )}

        <Card padding="p-0">
          {q.isLoading ? (
            <div className="text-center py-12 text-[13px] text-slate-400"><Loader2 className="inline w-4 h-4 animate-spin" /> Loading…</div>
          ) : rows.length === 0 ? (
            <EmptyState icon={Store} title="No merchants match" body="Try a different filter." />
          ) : (
            <table className="text-[13px] w-full block overflow-x-auto whitespace-nowrap sm:table sm:whitespace-normal">
              <thead className="text-[10.5px] uppercase tracking-wider text-slate-400 bg-slate-50">
                <tr>
                  <th className="text-left py-2.5 px-5 font-medium">Business</th>
                  <th className="text-left py-2.5 px-3 font-medium">Owner</th>
                  <th className="text-left py-2.5 px-3 font-medium">City</th>
                  <th className="text-left py-2.5 px-3 font-medium">KYB</th>
                  <th className="text-left py-2.5 px-3 font-medium">Applied</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((m) => <MerchantRow key={m.user_id} m={m} />)}
              </tbody>
            </table>
          )}
        </Card>

        <p className="text-[11.5px] text-slate-400">
          KYB decisions are audit-logged. OFAC/SDN sanctions screening at approval is a planned compliance step (not yet wired).
        </p>
      </div>
    </PortalShell>
  );
}

function CertDownload({ m }: { m: AdminMerchantRow }) {
  const [lang, setLang] = useState<CertLang>('en');
  const [busy, setBusy] = useState(false);
  if (m.status !== 'approved') return null;

  const dl = async () => {
    setBusy(true);
    try { await certificates.forMerchant(m.user_id, lang); }
    catch (e) { toast.error((e as Error).message || 'Download failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className="flex items-center gap-1">
      <select
        value={lang}
        onChange={(e) => setLang(e.target.value as CertLang)}
        className="text-[10.5px] border border-slate-200 rounded px-1 py-0.5 bg-white"
        aria-label="Certificate language"
      >
        <option value="en">EN</option><option value="fr">FR</option><option value="ht">HT</option>
      </select>
      <button onClick={dl} disabled={busy} className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-600 hover:text-slate-900">
        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />} Certificate
      </button>
    </div>
  );
}

function KybActions({ m }: { m: AdminMerchantRow }) {
  const qc = useQueryClient();
  const mut = useMutation({
    mutationFn: (v: { status: KybStatus; note?: string | null }) => admin.setMerchantStatus(m.user_id, v.status, v.note),
    onSuccess: (_res, v) => {
      toast.success(v.status === 'approved' ? `${m.business_name} approved` : `Merchant ${v.status.replace('_', ' ')}`);
      qc.invalidateQueries({ queryKey: ['admin', 'merchants'] });
    },
    onError: (e: Error) => toast.error(e.message || 'Update failed'),
  });

  const acts = KYB_ACTIONS[m.status] ?? [];
  if (acts.length === 0) return null;

  const run = (to: KybStatus) => {
    if (to === 'rejected') {
      const note = window.prompt('Reason for rejection (optional):');
      if (note === null) return;                 // cancelled
      mut.mutate({ status: to, note: note || null });
    } else if (to === 'suspended') {
      if (window.confirm(`Suspend ${m.business_name}? They'll disappear from the storefront immediately.`)) mut.mutate({ status: to });
    } else {
      mut.mutate({ status: to });
    }
  };

  return (
    <div className="flex items-center justify-end gap-1.5 flex-wrap">
      {acts.map((a) => (
        <Button
          key={a.to}
          size="sm"
          tone={a.tone}
          onClick={() => run(a.to)}
          loading={mut.isPending && mut.variables?.status === a.to}
          disabled={mut.isPending}
        >
          {a.label}
        </Button>
      ))}
    </div>
  );
}

function MerchantRow({ m }: { m: AdminMerchantRow }) {
  const created = m.user?.created_at ? new Date(m.user.created_at).toLocaleDateString() : '—';
  const detailHref = `/admin/merchants/${m.user_id}`;
  return (
    <tr className="hover:bg-slate-50">
      <td className="py-3 px-5">
        <Link to={detailHref} className="font-semibold text-slate-900 hover:underline">{m.business_name}</Link>
        {m.legal_name && <p className="text-[11.5px] text-slate-500">{m.legal_name}</p>}
        {m.merchant_code && <p className="text-[10.5px] font-mono text-slate-400 mt-0.5">{m.merchant_code}</p>}
      </td>
      <td className="py-3 px-3 text-slate-700">
        {m.user?.email && <p className="inline-flex items-center gap-1 text-[12px]"><Mail className="w-3 h-3 text-slate-400" /> {m.user.email}</p>}
        {m.user?.phone_e164 && <p className="inline-flex items-center gap-1 text-[11.5px] text-slate-500"><Phone className="w-3 h-3" /> {m.user.phone_e164}</p>}
      </td>
      <td className="py-3 px-3 text-slate-700 inline-flex items-center gap-1 mt-3">
        <MapPin className="w-3 h-3 text-slate-400" /> {m.city?.name ?? '—'}
      </td>
      <td className="py-3 px-3"><StatusPill status={m.status} /></td>
      <td className="py-3 px-3 text-slate-600 text-[12px]">{created}</td>
      <td className="py-3 px-5">
        <div className="flex flex-col items-end gap-1.5">
          <KybActions m={m} />
          <CertDownload m={m} />
          <Link to={detailHref} className="inline-flex items-center gap-0.5 text-[11.5px] font-semibold text-brand-600 hover:underline">
            View <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
      </td>
    </tr>
  );
}
